import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Loader from '../Loader'
import { signInWithOAuth } from '../../utils/authOAuth'
import { signOutClientSession } from '../../utils/authSignOut'
import {
  SIGNUP_SKIP_AUTO_REDIRECT_KEY,
  clearSignupSkipAutoRedirect,
} from '../../utils/signOutSignupFlow'
import SignupLayout from './SignupLayout'
import { readDraft } from './signupState'

/* ---------- Brand-monochrome inline OAuth icons ---------- */

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden className="flex-shrink-0">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
)

const MicrosoftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 23 23" aria-hidden className="flex-shrink-0">
    <path fill="#f35325" d="M1 1h10v10H1z" />
    <path fill="#81bc06" d="M12 1h10v10H12z" />
    <path fill="#05a6f0" d="M1 12h10v10H1z" />
    <path fill="#ffba08" d="M12 12h10v10H12z" />
  </svg>
)

const Spinner = () => <Loader size={18} />

/* ---------- Component ---------- */

/**
 * SignupAuth — Screen 2 of /signup (SSO). Light theme.
 *
 * Google / Microsoft sign-in via Supabase OAuth; returns to
 * /signup/callback to resume the funnel with the authenticated user.
 */
export default function SignupAuth() {
  const navigate = useNavigate()
  const location = useLocation()
  const draft = useMemo(readDraft, [])
  const persona = draft.persona || null

  const [oauthLoading, setOauthLoading] = useState(null) // 'google' | 'microsoft' | null
  const [oauthError, setOauthError] = useState(
    () => location.state?.oauthError || '',
  )

  useEffect(() => {
    (async () => {
      if (typeof window === 'undefined') return
      if (sessionStorage.getItem(SIGNUP_SKIP_AUTO_REDIRECT_KEY) !== '1') return

      await signOutClientSession()
      clearSignupSkipAutoRedirect()
    })()
  }, [])

  const headline =
    persona === 'community'
      ? 'Create your organizer account'
      : persona === 'academy'
        ? 'Create your coach account'
        : persona === 'venue'
          ? 'Create your venue account'
          : 'Create your account'

  const handleOAuth = async (provider) => {
    if (!persona) {
      navigate('/signup')
      return
    }

    setOauthError('')
    setOauthLoading(provider)

    const supabaseProvider = provider === 'microsoft' ? 'azure' : 'google'
    const redirectTo = `${window.location.origin}/signup/callback`
    const { error } = await signInWithOAuth(supabaseProvider, redirectTo)

    if (error) {
      console.error(provider, error)
      setOauthError(
        error.message || 'Could not start sign-in. Please try again.',
      )
      setOauthLoading(null)
    }
  }

  // Equal-weight neutral-outlined style shared by both SSO buttons.
  // Only the brand icon distinguishes them — no implicit hierarchy.
  const ssoButtonCls =
    'w-full h-[52px] rounded-[10px] bg-[var(--signup-bg-elevated)] border border-[var(--signup-border-default)] text-[var(--signup-text-primary)] font-medium text-[15px] flex items-center justify-center gap-3 hover:bg-[var(--signup-bg-subtle)] hover:border-[var(--signup-border-strong)] active:scale-[0.98] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--signup-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--signup-bg-base)] motion-safe:transition-[background-color,border-color,transform] motion-safe:duration-150'

  // Mobile-pinned secondary entry point. Desktop keeps the header
  // link rendered by SignupLayout (md:inline-block) and the layout
  // hides this slot below md.
  const mobileLogIn = (
    <p className="text-center text-[14px] text-[var(--signup-text-secondary)]">
      Already have an account?{' '}
      <Link
        to="/login"
        className="inline-flex items-center min-h-[44px] px-2 -my-2 text-[var(--signup-accent)] font-medium hover:underline underline-offset-4 motion-safe:transition-colors motion-safe:duration-150"
      >
        Log in
      </Link>
    </p>
  )

  return (
    <SignupLayout showBack backTo="/" centerOnMobile mobileBottomSlot={mobileLogIn}>
      {/* Headline + subhead */}
      <h1
        className="font-semibold text-[var(--signup-text-primary)] text-[30px] sm:text-[44px] leading-[1.15]"
        style={{ letterSpacing: '-0.01em' }}
      >
        {headline}
      </h1>
      <p className="mt-3 text-[15px] text-[var(--signup-text-secondary)] leading-[1.4] max-w-[420px]">
        We'll set up your portal in setup mode so you can explore right away.
      </p>

      {oauthError ? (
        <p
          role="alert"
          className="mt-6 text-[14px] text-red-600 leading-snug"
        >
          {oauthError}
        </p>
      ) : null}

      {/* SSO buttons — equal-weight neutral outlines */}
      <div className={`space-y-3 ${oauthError ? 'mt-4' : 'mt-8'}`}>
        <button
          type="button"
          onClick={() => handleOAuth('google')}
          disabled={!!oauthLoading}
          className={ssoButtonCls}
        >
          {oauthLoading === 'google' ? <Spinner /> : <GoogleIcon />}
          <span>Continue with Google</span>
        </button>

        <button
          type="button"
          onClick={() => handleOAuth('microsoft')}
          disabled={!!oauthLoading}
          className={ssoButtonCls}
        >
          {oauthLoading === 'microsoft' ? <Spinner /> : <MicrosoftIcon />}
          <span>Continue with Microsoft</span>
        </button>
      </div>

      {/* Fine print */}
      <p className="mt-4 text-[13px] text-[var(--signup-text-secondary)] leading-[1.55]">
        By continuing, you agree to our{' '}
        <a
          href="/terms"
          className="text-[var(--signup-accent)] hover:underline underline-offset-2 motion-safe:transition-colors motion-safe:duration-150"
        >
          Terms
        </a>{' '}
        and{' '}
        <a
          href="/privacy"
          className="text-[var(--signup-accent)] hover:underline underline-offset-2 motion-safe:transition-colors motion-safe:duration-150"
        >
          Privacy Policy
        </a>
        .
      </p>

      {/* Quiet reinforcement — what they'll get after signing in */}
      <div className="mt-8">
        <p
          className="text-[12px] font-medium uppercase text-[var(--signup-text-secondary)]"
          style={{ letterSpacing: '0.14em' }}
        >
          What you'll get
        </p>
        <ul className="mt-4 space-y-2">
          {[
            'Camera-ready setup in days',
            'Stats and highlights, automatically',
            'A real human walkthrough on activation',
          ].map((item) => (
            <li
              key={item}
              className="flex items-baseline gap-3 text-[14px] text-[var(--signup-text-primary)] leading-[1.5]"
            >
              <span aria-hidden className="text-[var(--signup-text-tertiary)] select-none">
                —
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </SignupLayout>
  )
}
